import { capitalize } from 'underscore.string';
import i18n from 'src/utils/translate';
import { useDispatch, useSelector } from 'src/store/store';
import postsFilterOptions from 'src/constants/postsFilterOptions';
import { months } from 'src/utils/date';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import { redirectToPostsPage, redirectToPostsTopicPage, redirectToPollsPage } from 'src/utils/router';
import { WriteLink } from 'src/modules/community/shared/components/writeButton';
import PostListLoader from 'src/modules/shared/resultsList/components/postListLoader';
import PostItem from 'src/modules/shared/resultsList/components/postItem';
import PollItem, { PollItemType } from 'src/modules/community/communityPosts/components/pollItem';
import CommunityFooterCTA from 'src/modules/community/shared/components/communityFooterCTA';
import PostFilterInline from 'src/components/postFilterInline';
import Pagination from 'src/components/pagination/pagination';
import SearchBox from 'src/modules/community/shared/components/searchBox';
import VisitorLoginPrompt from 'src/modules/shared/resultsList/components/visitorLoginPrompt';
import { fetchPosts } from 'src/modules/community/communityPosts/actions';
import Button from 'src/components/buttons/button';
import { ResultsSeeMoreCTA } from 'styles-js/resultsList';
import { CommunityListPost, ElasticSearchPost } from 'src/constants/types';

export default function CommunityPostsContent(): JSX.Element {
  const dispatch = useDispatch();
  const { currentCommunity, currentUser } = useSelector(({ context }) => context);

  const {
    archive = {},
    filter = 'latest',
    hasMorePosts,
    isPostListLoading,
    posts = [],
    selectedTopic = '',
    selectedTopicId,
    totalMonthlyPosts,
  } = useSelector(({ pages }) => pages.communityPosts);

  const handleFilterClick = (newFilter: string) => {
    if (selectedTopic) {
      redirectToPostsTopicPage({ communitySlug: currentCommunity.slug, topicName: encodeURIComponent(selectedTopic.toLowerCase()), filter: newFilter });
    } else if (newFilter === 'polls') {
      redirectToPollsPage(currentCommunity.slug);
    } else {
      redirectToPostsPage({ communitySlug: currentCommunity.slug, filter: newFilter });
    }

    trackFootfallEvent(Event.CLICKED, {
      clickType: 'community-posts-filter',
      clickMetadata: { section: Section.CommunityPosts, element: newFilter },
    });
  };

  const handleSeeMoreClick = () => {
    // Optional parameter at the end is a flag when the user clicks on this button
    dispatch(fetchPosts({
      communitySlug: currentCommunity.slug,
      filter,
      topicId: selectedTopicId,
      topicName: selectedTopic,
      isSeeMoreClick: true,
    }));

    trackFootfallEvent(Event.CLICKED, {
      clickType: 'see-more-posts',
      clickMetadata: { section: Section.CommunityPosts },
    });
  };

  function renderPostList() {
    const isPoll = filter === 'polls';
    if (posts.length > 0) {
      const orderedPosts = () => {
        const postsList = isPoll
          ? posts.map((poll: PollItemType) => <PollItem key={poll.postId} poll={poll} />)
          : posts.map((post: CommunityListPost | ElasticSearchPost) => (
            <PostItem
              community={currentCommunity}
              isSearchPost={false}
              key={post.postId}
              post={post}
            />
          ));
        if (!currentUser) {
          return [...postsList.slice(0, 3), <VisitorLoginPrompt key="visitor-prompt" />, ...postsList.slice(3)];
        } else {
          return postsList;
        }
      };

      return (
        <div className="results-posts" data-testid="results-posts">
          {orderedPosts()}
          {hasMorePosts && !archive.currentYear && (
            <ResultsSeeMoreCTA>
              <Button
                type="secondary"
                onClick={handleSeeMoreClick}
              >
                {isPoll ? i18n.t('See more polls') : i18n.t('See more posts')}
              </Button>
            </ResultsSeeMoreCTA>
          )}
        </div>
      );
    } else {
      return (
        !currentCommunity.isArchived && (
          <div className="community__home-empty">
            {isPoll
              ? i18n.t('No polls yet, check back soon')
              : (
                <>
                  {i18n.t('Nothing here yet.')}
                  {' '}
                  <WriteLink trackingSection={Section.CommunityPosts}>{i18n.t('Share your experience!')}</WriteLink>
                </>
              )
            }
          </div>
        )
      );
    }
  }

  const currentMonth = months.find(momentMonth => momentMonth.twoDigits === archive.currentMonth);

  return (
    <div className="community-posts__content">
      {!!posts.length && (
        <SearchBox
          isCommunitySearch={true}
          isMobile={true}
          placeholder={i18n.t('Search posts on ') + currentCommunity.name}
        />
      )}
      {selectedTopic && (
        <div className="community-posts__heading" data-testid="community-posts-heading">
          <h3>{capitalize(selectedTopic)}{' '}{i18n.t('posts')}</h3>
        </div>
      )}

      {archive.currentYear
        ? (
          <div className="community-posts__heading" data-testid="community-posts-heading">
            <h3>{i18n.t('All posts for')} {currentMonth && i18n.t(currentMonth.name)} {archive.currentYear}</h3>
          </div>
        ) : (
          <div className="community-posts__filters" data-testid="community-posts-filters">
            <p className="community-posts__filters-heading">{i18n.t('Sort posts by')}:</p>
            <PostFilterInline
              options={postsFilterOptions}
              selected={filter}
              onFilterClick={handleFilterClick}
            />
          </div>
        )
      }

      {isPostListLoading
        ? <PostListLoader />
        : renderPostList()
      }

      {!archive.currentYear && <CommunityFooterCTA />}

      {archive.currentYear && totalMonthlyPosts > 0 && (
        <div data-testid="search-pagination">
          <Pagination
            isShowBotLinks={true}
            total={totalMonthlyPosts}
          />
        </div>
      )}
    </div>
  );
}
